/**
 * Principais plataformas de e-commerce no mercado brasileiro/LATAM.
 * Ordem: relevância comercial (enterprise → SMB → marketplaces → legado).
 */
export const ECOMMERCE_PLATFORMS = [
  'Shopify',
  'VTEX',
  'Linx',
  'Nuvemshop',
  'Tray',
  'WooCommerce',
  'Magento',
  'Loja Integrada',
  'Wix',
  'WordPress',
  'Mercado Livre',
  'Amazon',
  'Wake Commerce',
  'Uappi',
  'BigCommerce',
  'Salesforce Commerce Cloud',
  'Irroba',
  'Bagy',
  'OpenCart',
  'PrestaShop',
  'Landing Page',
] as const

/** Opção do select — abre campo para digitar plataforma fora da lista. */
export const ECOMMERCE_PLATFORM_OTHER_OPTION = 'Outra (informar)'

const CUSTOM_PLATFORMS_STORAGE_KEY = 'crm:custom-ecommerce-platforms'

export function loadCustomEcommercePlatforms(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(CUSTOM_PLATFORMS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
  } catch {
    return []
  }
}

export function saveCustomEcommercePlatforms(platforms: string[]): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(CUSTOM_PLATFORMS_STORAGE_KEY, JSON.stringify(platforms))
}

export function findEcommercePlatformDuplicate(
  name: string,
  customPlatforms: string[] = loadCustomEcommercePlatforms(),
): string | null {
  const trimmed = name.trim()
  if (!trimmed) return null
  const lower = trimmed.toLowerCase()
  const all = [...ECOMMERCE_PLATFORMS, ...customPlatforms]
  return all.find((platform) => platform.toLowerCase() === lower) ?? null
}

export function addCustomEcommercePlatform(
  name: string,
): { ok: true; platform: string } | { ok: false; error: string } {
  const trimmed = name.trim()
  if (!trimmed) return { ok: false, error: 'Informe o nome da plataforma' }

  const duplicate = findEcommercePlatformDuplicate(trimmed)
  if (duplicate) {
    return { ok: false, error: `A plataforma "${duplicate}" já existe na lista` }
  }

  const custom = loadCustomEcommercePlatforms()
  custom.push(trimmed)
  saveCustomEcommercePlatforms(custom)
  return { ok: true, platform: trimmed }
}

export function getEcommercePlatformSelectOptions(customPlatforms: string[] = loadCustomEcommercePlatforms()): string[] {
  const listed = [...ECOMMERCE_PLATFORMS]
  const extra = customPlatforms.filter((name) => !isListedEcommercePlatform(name))
  return [...listed, ...extra, ECOMMERCE_PLATFORM_OTHER_OPTION]
}

export const ECOMMERCE_PLATFORM_SELECT_OPTIONS = [
  ...ECOMMERCE_PLATFORMS,
  ECOMMERCE_PLATFORM_OTHER_OPTION,
] as const

export type EcommercePlatform = (typeof ECOMMERCE_PLATFORMS)[number]

export function isListedEcommercePlatform(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return false
  return (ECOMMERCE_PLATFORMS as readonly string[]).includes(trimmed)
}

export function platformSelectValue(
  stored: string | null | undefined,
  customPlatforms: string[] = loadCustomEcommercePlatforms(),
): string {
  const trimmed = (stored ?? '').trim()
  if (!trimmed) return ''
  if (isListedEcommercePlatform(trimmed)) return trimmed
  const customMatch = customPlatforms.find((platform) => platform.toLowerCase() === trimmed.toLowerCase())
  if (customMatch) return customMatch
  return ECOMMERCE_PLATFORM_OTHER_OPTION
}

export function isCustomEcommercePlatformSelect(selectValue: string): boolean {
  return selectValue === ECOMMERCE_PLATFORM_OTHER_OPTION
}

/** Aceita valor legado salvo no banco e normaliza para opção do select (se possível). */
export function normalizeEcommercePlatform(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return ''

  const lower = trimmed.toLowerCase()
  const match = ECOMMERCE_PLATFORMS.find((p) => p.toLowerCase() === lower)
  if (match) return match

  const aliases: Record<string, EcommercePlatform> = {
    'linx commerce': 'Linx',
    linxcommerce: 'Linx',
    'adobe commerce': 'Magento',
    'mercado shops': 'Mercado Livre',
    'mercado livre shops': 'Mercado Livre',
    salesforce: 'Salesforce Commerce Cloud',
    'commerce cloud': 'Salesforce Commerce Cloud',
    'loja integrada': 'Loja Integrada',
    wake: 'Wake Commerce',
    'digital manager guru': 'Wake Commerce',
    woocommerce: 'WooCommerce',
    'woo commerce': 'WooCommerce',
    'wordpress / woocommerce': 'WooCommerce',
    landing: 'Landing Page',
    'landing page institucional': 'Landing Page',
  }

  if (['outro', 'outra', 'outras', 'other'].includes(lower)) return ''

  return aliases[lower] ?? trimmed
}
